import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumCreditType } from "./enums/enumCreditType.generated";
import { EnumUkDutyReturnType } from "./enums/enumUkDutyReturnType.generated";
import { EnumUllageReturnMethod } from "./enums/enumUllageReturnMethod.generated";
import { EnumUllageStatus } from "./enums/enumUllageStatus.generated";

// Model Objects

export class UllageHeaderReadOnly extends ModelObject<UllageHeaderReadOnly> {

    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which ullage was marked complete (if completed). Captured by system not user editable.
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }
    /**
    * User who created the ullage. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Ullage Number
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public ullageNumber: string = "";
    /**
    * Timestamp at which the ullage was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the ullage was cancelled on the system (if cancelled). Captured by system not user editable.
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    private _cancelledByUserId: Guid | null = null;
    /**
    * User who cancelled the ullage (if cancelled). Captured by system not user editable.
    */
    public get cancelledByUserId(): Guid | null { return this._cancelledByUserId }
    public set cancelledByUserId(value: Guid | null) {
      this._cancelledByUserId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.ullageNumber = jsonSerializedObject.ullageNumber;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.cancelledByUserId = jsonSerializedObject.cancelledByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.cancelledByUserId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.ullageNumber = this.ullageNumber;
        toRet.createdAt = this.createdAt.toString();
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.cancelledByUserId = this.cancelledByUserId == null ? null : this.cancelledByUserId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "UllageHeaderReadOnly.CreatedByUserId cannot be empty"},
            {field: "cancelledByUserId", rule: (v: any) => v == null || !v.isEmpty() || "UllageHeaderReadOnly.CancelledByUserId cannot be empty"},
    ];
}


export class UllageHeader extends ModelObject<UllageHeader> {

    public version: number = 0;
    /**
    * Notes relating to the authorisation of a credit.
    * Not Nullable
    * Max Length : 100000
    */
    public creditAuthorisationNotes: string = "";
    private _creditAuthorisedByUserId: Guid | null = null;
    /**
    * User who authorised the creation of a credit for this ullage (if any).
    */
    public get creditAuthorisedByUserId(): Guid | null { return this._creditAuthorisedByUserId }
    public set creditAuthorisedByUserId(value: Guid | null) {
      this._creditAuthorisedByUserId = value === undefined ? null : value;
    }
    /**
    * Credit Type to Create. : 0 = None, 1 = CreditNoDutyReclaim, 2 = DutyOnlyReclaim, 3 = CreditWithDutyReclaim
    */
    public creditTypeToCreate: EnumCreditType = EnumCreditType.none;
    /**
    * Customer Reference
    * Not Nullable
    * Max Length : 1000
    */
    public customerReference: string = "";
    public id: Guid = Guid.createEmpty();
    /**
    * Number of Items reported by the outlet as being retuned.
    */
    public numberOfItemsReported: number = 0;
    /**
    * Our Reference
    * Not Nullable
    * Max Length : 1000
    */
    public ourReference: string = "";
    private _pickupDate: LocalDate | null = null;
    /**
    * Date on which the Ullage should be picked up. Required when ReturnMethod=Pickup
    */
    public get pickupDate(): LocalDate | null { return this._pickupDate }
    public set pickupDate(value: LocalDate | null) {
      this._pickupDate = value === undefined ? null : value;
    }
    /**
    * Notes for the driver performing Pickup.
    * Not Nullable
    * Max Length : 100000
    */
    public pickupNotesForDriver: string = "";
    /**
    * The expected duration required for the pickup.
    */
    public pickupStopMinutes: number = 0;
    private _pickupWindowEnd: LocalTime | null = null;
    /**
    * The end time of the Pickup window. Optional even if ReturnMethod=Pickup
    */
    public get pickupWindowEnd(): LocalTime | null { return this._pickupWindowEnd }
    public set pickupWindowEnd(value: LocalTime | null) {
      this._pickupWindowEnd = value === undefined ? null : value;
    }
    private _pickupWindowStart: LocalTime | null = null;
    /**
    * The start time of the Pickup window. Optional even if ReturnMethod=Pickup
    */
    public get pickupWindowStart(): LocalTime | null { return this._pickupWindowStart }
    public set pickupWindowStart(value: LocalTime | null) {
      this._pickupWindowStart = value === undefined ? null : value;
    }
    /**
    * Ullage Return Method : 0 = None, 1 = DriverPickup, 2 = Courier, 3 = CustomerDropOff
    */
    public returnMethod: EnumUllageReturnMethod = EnumUllageReturnMethod.none;
    /**
    * Expected Weight to be picked up.
    * Not Negative
    */
    public pickupWeightKg: number = 0;
    public readOnly: UllageHeaderReadOnly = new UllageHeaderReadOnly();
    private _originalDutyPaidDate: LocalDate | null = null;
    /**
    * Date when duty was originally paid. Null if duty was not paid (eg is being ullaged from a bonded stock location). Required for Outlet ullages and ullages from non-bonded stock locations.
    */
    public get originalDutyPaidDate(): LocalDate | null { return this._originalDutyPaidDate }
    public set originalDutyPaidDate(value: LocalDate | null) {
      this._originalDutyPaidDate = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet which reported the Ullage, if this is an outlet ullage.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _ullageFromStockLocationId: Guid | null = null;
    /**
    * Contains the stock location from which the beer is being destroyed, if this is direct destruction from stock.
    */
    public get ullageFromStockLocationId(): Guid | null { return this._ullageFromStockLocationId }
    public set ullageFromStockLocationId(value: Guid | null) {
      this._ullageFromStockLocationId = value === undefined ? null : value;
    }
    /**
    * Ullage Reason
    * Not Nullable
    * Max Length : 1000
    */
    public ullageReason: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.creditAuthorisationNotes = jsonSerializedObject.creditAuthorisationNotes;
          this.creditAuthorisedByUserId = jsonSerializedObject.creditAuthorisedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.creditAuthorisedByUserId);
          this.creditTypeToCreate = EnumCreditType.getByValue(jsonSerializedObject.creditTypeToCreate, this.creditTypeToCreate);
          this.customerReference = jsonSerializedObject.customerReference;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.numberOfItemsReported = jsonSerializedObject.numberOfItemsReported;
          this.ourReference = jsonSerializedObject.ourReference;
          this.pickupDate = jsonSerializedObject.pickupDate == null ? null : LocalDate.parse(jsonSerializedObject.pickupDate);
          this.pickupNotesForDriver = jsonSerializedObject.pickupNotesForDriver;
          this.pickupStopMinutes = jsonSerializedObject.pickupStopMinutes;
          this.pickupWindowEnd = jsonSerializedObject.pickupWindowEnd == null ? null : LocalTime.parse(jsonSerializedObject.pickupWindowEnd);
          this.pickupWindowStart = jsonSerializedObject.pickupWindowStart == null ? null : LocalTime.parse(jsonSerializedObject.pickupWindowStart);
          this.returnMethod = EnumUllageReturnMethod.getByValue(jsonSerializedObject.returnMethod, this.returnMethod);
          this.pickupWeightKg = jsonSerializedObject.pickupWeightKg;
          this.readOnly = new UllageHeaderReadOnly(jsonSerializedObject.readOnly);
          this.originalDutyPaidDate = jsonSerializedObject.originalDutyPaidDate == null ? null : LocalDate.parse(jsonSerializedObject.originalDutyPaidDate);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.ullageFromStockLocationId = jsonSerializedObject.ullageFromStockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.ullageFromStockLocationId);
          this.ullageReason = jsonSerializedObject.ullageReason;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.creditAuthorisationNotes = this.creditAuthorisationNotes;
        toRet.creditAuthorisedByUserId = this.creditAuthorisedByUserId == null ? null : this.creditAuthorisedByUserId.toString();
        toRet.creditTypeToCreate = this.creditTypeToCreate.value;
        toRet.customerReference = this.customerReference;
        toRet.id = this.id.toString();
        toRet.numberOfItemsReported = this.numberOfItemsReported;
        toRet.ourReference = this.ourReference;
        toRet.pickupDate = this.pickupDate == null ? null : this.pickupDate.toString();
        toRet.pickupNotesForDriver = this.pickupNotesForDriver;
        toRet.pickupStopMinutes = this.pickupStopMinutes;
        toRet.pickupWindowEnd = this.pickupWindowEnd == null ? null : this.pickupWindowEnd.toString();
        toRet.pickupWindowStart = this.pickupWindowStart == null ? null : this.pickupWindowStart.toString();
        toRet.returnMethod = this.returnMethod.value;
        toRet.pickupWeightKg = this.pickupWeightKg;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.originalDutyPaidDate = this.originalDutyPaidDate == null ? null : this.originalDutyPaidDate.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.ullageFromStockLocationId = this.ullageFromStockLocationId == null ? null : this.ullageFromStockLocationId.toString();
        toRet.ullageReason = this.ullageReason;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "creditAuthorisationNotes", rule: (v: any) => v == null || v.length <= 100000 || "CreditAuthorisationNotes has too many characters (100000)"},
            {field: "creditAuthorisedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "UllageHeader.CreditAuthorisedByUserId cannot be empty"},
            {field: "customerReference", rule: (v: any) => v == null || v.length <= 1000 || "CustomerReference has too many characters (1000)"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "UllageHeader.Id cannot be empty"},
            {field: "numberOfItemsReported", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfItemsReported is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfItemsReported", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfItemsReported is above maximum allowed value: 2147483647"},
            {field: "ourReference", rule: (v: any) => v == null || v.length <= 1000 || "OurReference has too many characters (1000)"},
            {field: "pickupNotesForDriver", rule: (v: any) => v == null || v.length <= 100000 || "PickupNotesForDriver has too many characters (100000)"},
            {field: "pickupStopMinutes", rule: (v: any) => v == null || v >= -2147483648 || "PickupStopMinutes is not greater than minimum allowed value: -2147483648"},
            {field: "pickupStopMinutes", rule: (v: any) => v == null || v <= 2147483647 || "PickupStopMinutes is above maximum allowed value: 2147483647"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "UllageHeader.OutletId cannot be empty"},
            {field: "ullageFromStockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "UllageHeader.UllageFromStockLocationId cannot be empty"},
            {field: "ullageReason", rule: (v: any) => v == null || v.length <= 1000 || "UllageReason has too many characters (1000)"},
    ];
}


export class UllageLineReadOnly extends ModelObject<UllageLineReadOnly> {

    private _spoiltClaimDutyReturnId: Guid | null = null;
    /**
    * Spoilt Beer Return Id
    */
    public get spoiltClaimDutyReturnId(): Guid | null { return this._spoiltClaimDutyReturnId }
    public set spoiltClaimDutyReturnId(value: Guid | null) {
      this._spoiltClaimDutyReturnId = value === undefined ? null : value;
    }
    /**
    * Duty Type of the SpoiltClaimDutyReturnId
    */
    public spoiltClaimDutyReturnType: EnumUkDutyReturnType = EnumUkDutyReturnType.none;
    private _manufacturerId: Guid | null = null;
    /**
    * Manufacturer of the product - Required for Uk Beer Duty products.
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    /**
    * Dutiable Volume (litres) of a single unit of this product
    * Not Negative
    */
    public unitDutiableLitres: number = 0;
    private _unitLitres: number | null = null;
    /**
    * Volume (litres) of a single unit of this product, if known. Ullage cannot be processed if not known.
    * Not Negative
    */
    public get unitLitres(): number | null { return this._unitLitres }
    public set unitLitres(value: number | null) {
      this._unitLitres = value === undefined ? null : value;
    }
    /**
    * Header to which this line belongs.
    */
    public ullageHeaderId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.spoiltClaimDutyReturnId = jsonSerializedObject.spoiltClaimDutyReturnId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.spoiltClaimDutyReturnId);
          this.spoiltClaimDutyReturnType = EnumUkDutyReturnType.getByValue(jsonSerializedObject.spoiltClaimDutyReturnType, this.spoiltClaimDutyReturnType);
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres;
          this.unitLitres = jsonSerializedObject.unitLitres == null ? null : jsonSerializedObject.unitLitres;
          this.ullageHeaderId = Guid.parseOrEmpty(jsonSerializedObject.ullageHeaderId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.spoiltClaimDutyReturnId = this.spoiltClaimDutyReturnId == null ? null : this.spoiltClaimDutyReturnId.toString();
        toRet.spoiltClaimDutyReturnType = this.spoiltClaimDutyReturnType.value;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.unitDutiableLitres = this.unitDutiableLitres;
        toRet.unitLitres = this.unitLitres == null ? null : this.unitLitres;
        toRet.ullageHeaderId = this.ullageHeaderId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "spoiltClaimDutyReturnId", rule: (v: any) => v == null || !v.isEmpty() || "UllageLineReadOnly.SpoiltClaimDutyReturnId cannot be empty"},
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "UllageLineReadOnly.ManufacturerId cannot be empty"},
            {field: "ullageHeaderId", rule: (v: any) => !v.isEmpty() || "UllageLineReadOnly.UllageHeaderId cannot be empty"},
    ];
}


export class UllageLine extends ModelObjectWithDirtyCheck<UllageLine> {

    public version: number = 0;
    /**
    * Product ABV
    * Not Negative
    */
    public abv: number = 0;
    /**
    * Alcohol Type
    */
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    private _containerId: Guid | null = null;
    /**
    * The trackable container (if any and known) which contains the ullaged goods.
    */
    public get containerId(): Guid | null { return this._containerId }
    public set containerId(value: Guid | null) {
      this._containerId = value === undefined ? null : value;
    }
    private _destroyedAt: Instant | null = null;
    /**
    * Timestamp at which destruction occurred.
    */
    public get destroyedAt(): Instant | null { return this._destroyedAt }
    public set destroyedAt(value: Instant | null) {
      this._destroyedAt = value === undefined ? null : value;
    }
    /**
    * Notes relating to the destruction of the spoilt goods.
    * Not Nullable
    * Max Length : 100000
    */
    public destructionNotes: string = "";
    public id: Guid = Guid.createEmpty();
    private _dateReturned: LocalDate | null = null;
    /**
    * Date on which the item was returned (if it has been returned).
    */
    public get dateReturned(): LocalDate | null { return this._dateReturned }
    public set dateReturned(value: LocalDate | null) {
      this._dateReturned = value === undefined ? null : value;
    }
    /**
    * Notes from production checking the container.
    * Not Nullable
    * Max Length : 100000
    */
    public productionCheck: string = "";
    /**
    * Product which is being ullaged.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * The ullage label scanned for this returned item (if any).
    * Not Nullable
    * Max Length : 1000
    */
    public ullageLabel: string = "";
    /**
    * The Urn of the item being ullaged.
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    /**
    * Volume (litres) of spoilt liquid being Ullaged
    * Not Negative
    */
    public volumeLitres: number = 0;
    public readOnly: UllageLineReadOnly = new UllageLineReadOnly();
    /**
    * Ullage Reason if different to the header.
    * Not Nullable
    * Max Length : 1000
    */
    public ullageReason: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.abv = jsonSerializedObject.abv;
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.containerId = jsonSerializedObject.containerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.containerId);
          this.destroyedAt = jsonSerializedObject.destroyedAt == null ? null : Instant.parse(jsonSerializedObject.destroyedAt);
          this.destructionNotes = jsonSerializedObject.destructionNotes;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.dateReturned = jsonSerializedObject.dateReturned == null ? null : LocalDate.parse(jsonSerializedObject.dateReturned);
          this.productionCheck = jsonSerializedObject.productionCheck;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.ullageLabel = jsonSerializedObject.ullageLabel;
          this.urn = jsonSerializedObject.urn;
          this.volumeLitres = jsonSerializedObject.volumeLitres;
          this.readOnly = new UllageLineReadOnly(jsonSerializedObject.readOnly);
          this.ullageReason = jsonSerializedObject.ullageReason;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.abv = this.abv;
        toRet.alcoholType = this.alcoholType.value;
        toRet.containerId = this.containerId == null ? null : this.containerId.toString();
        toRet.destroyedAt = this.destroyedAt == null ? null : this.destroyedAt.toString();
        toRet.destructionNotes = this.destructionNotes;
        toRet.id = this.id.toString();
        toRet.dateReturned = this.dateReturned == null ? null : this.dateReturned.toString();
        toRet.productionCheck = this.productionCheck;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.ullageLabel = this.ullageLabel;
        toRet.urn = this.urn;
        toRet.volumeLitres = this.volumeLitres;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.ullageReason = this.ullageReason;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "containerId", rule: (v: any) => v == null || !v.isEmpty() || "UllageLine.ContainerId cannot be empty"},
            {field: "destructionNotes", rule: (v: any) => v == null || v.length <= 100000 || "DestructionNotes has too many characters (100000)"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "UllageLine.Id cannot be empty"},
            {field: "productionCheck", rule: (v: any) => v == null || v.length <= 100000 || "ProductionCheck has too many characters (100000)"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "UllageLine.StockItemId cannot be empty"},
            {field: "ullageLabel", rule: (v: any) => v == null || v.length <= 1000 || "UllageLabel has too many characters (1000)"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "ullageReason", rule: (v: any) => v == null || v.length <= 1000 || "UllageReason has too many characters (1000)"},
    ];
}


export class Ullage extends ModelObjectWithDirtyCheck<Ullage> {

    public header: UllageHeader = new UllageHeader();
    public lines: (UllageLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new UllageHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new UllageLine(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as UllageLine[]).reduce((acc: Array<string>, curr: UllageLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class UllageBulkAddItem extends ModelObject<UllageBulkAddItem> {

    public ullageHeaderId: Guid = Guid.createEmpty();
    public stockItemId: Guid = Guid.createEmpty();
    private _containerId: Guid | null = null;
    public get containerId(): Guid | null { return this._containerId }
    public set containerId(value: Guid | null) {
      this._containerId = value === undefined ? null : value;
    }
    private _urn: string | null = null;
    public get urn(): string | null { return this._urn }
    public set urn(value: string | null) {
      this._urn = value === undefined ? null : value;
    }
    public quantity: number = 0;
    private _ullageLabel: string | null = null;
    public get ullageLabel(): string | null { return this._ullageLabel }
    public set ullageLabel(value: string | null) {
      this._ullageLabel = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ullageHeaderId = Guid.parseOrEmpty(jsonSerializedObject.ullageHeaderId);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.containerId = jsonSerializedObject.containerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.containerId);
          this.urn = jsonSerializedObject.urn == null ? null : jsonSerializedObject.urn;
          this.quantity = jsonSerializedObject.quantity;
          this.ullageLabel = jsonSerializedObject.ullageLabel == null ? null : jsonSerializedObject.ullageLabel;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ullageHeaderId = this.ullageHeaderId.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.containerId = this.containerId == null ? null : this.containerId.toString();
        toRet.urn = this.urn == null ? null : this.urn;
        toRet.quantity = this.quantity;
        toRet.ullageLabel = this.ullageLabel == null ? null : this.ullageLabel;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ullageHeaderId", rule: (v: any) => !v.isEmpty() || "UllageBulkAddItem.UllageHeaderId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "UllageBulkAddItem.StockItemId cannot be empty"},
            {field: "containerId", rule: (v: any) => v == null || !v.isEmpty() || "UllageBulkAddItem.ContainerId cannot be empty"},
    ];
}


export class FilteredUllagesResult extends ModelObject<FilteredUllagesResult> {

    public matchingCount: number = 0;
    public ullages: (Ullage)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.ullages = jsonSerializedObject.ullages.map((v: any) => new Ullage(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.ullages = this.ullages.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "ullages", rule: (v: any) => !!v || "ullages is required"},
            {field: "ullages", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Ullage[]).reduce((acc: Array<string>, curr: Ullage) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class UllageFilter extends ModelObject<UllageFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) ullage pickup date
    */
    public get fromDate(): LocalDate | null { return this._fromDate }
    public set fromDate(value: LocalDate | null) {
      this._fromDate = value === undefined ? null : value;
    }
    private _toDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) ullage pickup date
    */
    public get toDate(): LocalDate | null { return this._toDate }
    public set toDate(value: LocalDate | null) {
      this._toDate = value === undefined ? null : value;
    }
    private _fromCreatedDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) ullage created date
    */
    public get fromCreatedDate(): LocalDate | null { return this._fromCreatedDate }
    public set fromCreatedDate(value: LocalDate | null) {
      this._fromCreatedDate = value === undefined ? null : value;
    }
    private _toCreatedDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) ullage created date
    */
    public get toCreatedDate(): LocalDate | null { return this._toCreatedDate }
    public set toCreatedDate(value: LocalDate | null) {
      this._toCreatedDate = value === undefined ? null : value;
    }
    private _outletIds: readonly (Guid)[] | null = null;
    public get outletIds(): readonly (Guid)[] | null { return this._outletIds }
    public set outletIds(value: readonly (Guid)[] | null) {
      this._outletIds = value === undefined ? null : value;
    }
    private _statuses: readonly (EnumUllageStatus)[] | null = null;
    public get statuses(): readonly (EnumUllageStatus)[] | null { return this._statuses }
    public set statuses(value: readonly (EnumUllageStatus)[] | null) {
      this._statuses = value === undefined ? null : value;
    }
    private _ullageIds: readonly (Guid)[] | null = null;
    public get ullageIds(): readonly (Guid)[] | null { return this._ullageIds }
    public set ullageIds(value: readonly (Guid)[] | null) {
      this._ullageIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromDate = jsonSerializedObject.fromDate == null ? null : LocalDate.parse(jsonSerializedObject.fromDate);
          this.toDate = jsonSerializedObject.toDate == null ? null : LocalDate.parse(jsonSerializedObject.toDate);
          this.fromCreatedDate = jsonSerializedObject.fromCreatedDate == null ? null : LocalDate.parse(jsonSerializedObject.fromCreatedDate);
          this.toCreatedDate = jsonSerializedObject.toCreatedDate == null ? null : LocalDate.parse(jsonSerializedObject.toCreatedDate);
          this.outletIds = jsonSerializedObject.outletIds == null ? null : jsonSerializedObject.outletIds.map((v: any) => Guid.parseOrEmpty(v));
          this.statuses = jsonSerializedObject.statuses == null ? null : jsonSerializedObject.statuses.map((v: any) => EnumUllageStatus.getByValue(v));
          this.ullageIds = jsonSerializedObject.ullageIds == null ? null : jsonSerializedObject.ullageIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromDate = this.fromDate == null ? null : this.fromDate.toString();
        toRet.toDate = this.toDate == null ? null : this.toDate.toString();
        toRet.fromCreatedDate = this.fromCreatedDate == null ? null : this.fromCreatedDate.toString();
        toRet.toCreatedDate = this.toCreatedDate == null ? null : this.toCreatedDate.toString();
        toRet.outletIds = this.outletIds == null ? null : this.outletIds.map(v => v.toString());
        toRet.statuses = this.statuses == null ? null : this.statuses.map(v => v.value);
        toRet.ullageIds = this.ullageIds == null ? null : this.ullageIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class SpoiltItemLine extends ModelObject<SpoiltItemLine> {

    public header: UllageHeader = new UllageHeader();
    public line: UllageLine = new UllageLine();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new UllageHeader(jsonSerializedObject.header);
          this.line = new UllageLine(jsonSerializedObject.line);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.line = this.line.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "line", rule: (v: any) => !!v || "line is required"},
            {field: "line", rule: (v: any) => v.validate()},
    ];
}


export class UllageLineApproximateDuty extends ModelObject<UllageLineApproximateDuty> {

    public lineId: Guid = Guid.createEmpty();
    public approximateDuty: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.lineId = Guid.parseOrEmpty(jsonSerializedObject.lineId);
          this.approximateDuty = jsonSerializedObject.approximateDuty;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.lineId = this.lineId.toString();
        toRet.approximateDuty = this.approximateDuty;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "lineId", rule: (v: any) => !v.isEmpty() || "UllageLineApproximateDuty.LineId cannot be empty"},
    ];
}


export class UllageApproximateDuty extends ModelObject<UllageApproximateDuty> {

    public ullageId: Guid = Guid.createEmpty();
    public approximateDuty: number = 0;
    public lines: (UllageLineApproximateDuty)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ullageId = Guid.parseOrEmpty(jsonSerializedObject.ullageId);
          this.approximateDuty = jsonSerializedObject.approximateDuty;
          this.lines = jsonSerializedObject.lines.map((v: any) => new UllageLineApproximateDuty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ullageId = this.ullageId.toString();
        toRet.approximateDuty = this.approximateDuty;
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ullageId", rule: (v: any) => !v.isEmpty() || "UllageApproximateDuty.UllageId cannot be empty"},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as UllageLineApproximateDuty[]).reduce((acc: Array<string>, curr: UllageLineApproximateDuty) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class UllageService extends ServiceBase {

  public async createUllage(header: UllageHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/createUllage", req, true, extractor, opts);
  };

  public async getAllUllageHeaders(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UllageHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.ullageHeaders.map((v: any) => new UllageHeader(v));;
    return this.makeJsonRequest("ullage/v1/getAllUllageHeaders", req, false, extractor, opts);
  };

  public async updateUllageHeader(header: UllageHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    header:header.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/updateUllageHeader", req, true, extractor, opts);
  };

  public async getUllage(ullageHeaderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderId:ullageHeaderId.toString(),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/getUllage", req, false, extractor, opts);
  };

  public async cancelUllage(ullageHeaderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderId:ullageHeaderId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("ullage/v1/cancelUllage", req, true, extractor, opts);
  };

  public async completeUllage(ullageHeaderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderId:ullageHeaderId.toString(),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/completeUllage", req, true, extractor, opts);
  };

  public async saveUllageLine(ullageHeaderId: Guid, line: UllageLine, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderId:ullageHeaderId.toString(),
    line:line.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/saveUllageLine", req, true, extractor, opts);
  };

  public async createCreditForUllage(ullageHeaderId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderId:ullageHeaderId.toString(),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/createCreditForUllage", req, true, extractor, opts);
  };

  public async saveUllageLines(ullageHeaderId: Guid, ullageLines: readonly UllageLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderId:ullageHeaderId.toString(),
    ullageLines:ullageLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/saveUllageLines", req, true, extractor, opts);
  };

  public async deleteUllageLine(ullageHeaderId: Guid, ullageLineId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Ullage> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderId:ullageHeaderId.toString(),
    ullageLineId:ullageLineId.toString(),
    };
    const extractor = (response:any) => new Ullage(response.ullage);;
    return this.makeJsonRequest("ullage/v1/deleteUllageLine", req, true, extractor, opts);
  };

  public async bulkAddReturnedItemsToUllages(items: readonly UllageBulkAddItem[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    items:items.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("ullage/v1/bulkAddReturnedItemsToUllages", req, true, extractor, opts);
  };

  public async getUllageHeaders(ullageHeaderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UllageHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderIds:ullageHeaderIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.ullageHeaders.map((v: any) => new UllageHeader(v));;
    return this.makeJsonRequest("ullage/v1/getUllageHeaders", req, false, extractor, opts);
  };

  public async getUllagesByFilter(filter: UllageFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredUllagesResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredUllagesResult(response.results);;
    return this.makeJsonRequest("ullage/v1/getUllagesByFilter", req, false, extractor, opts);
  };

  public async getSpoiltItemDutyReturnIds(limitToGivenReturns: boolean, limitReturnIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Guid)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    limitToGivenReturns:limitToGivenReturns,
    limitReturnIds:limitReturnIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.returnIds.map((v: any) => Guid.parseOrEmpty(v));;
    return this.makeJsonRequest("ullage/v1/getSpoiltItemDutyReturnIds", req, false, extractor, opts);
  };

  public async getUnclaimedSpoiltItems(includeOpenUllages: boolean, limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SpoiltItemLine)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    includeOpenUllages:includeOpenUllages,
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new SpoiltItemLine(v));;
    return this.makeJsonRequest("ullage/v1/getUnclaimedSpoiltItems", req, false, extractor, opts);
  };

  public async getClaimedSpoiltItems(limitToGivenReturns: boolean, limitReturnIds: readonly Guid[], limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SpoiltItemLine)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    limitToGivenReturns:limitToGivenReturns,
    limitReturnIds:limitReturnIds.map(v => v.toString()),
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new SpoiltItemLine(v));;
    return this.makeJsonRequest("ullage/v1/getClaimedSpoiltItems", req, false, extractor, opts);
  };

  public async claimSpoiltItemsOnDutyReturn(ullageLineIds: readonly Guid[], dutyReturnId: Guid, dutyReturnType: EnumUkDutyReturnType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageLineIds:ullageLineIds.map(v => v.toString()),
    dutyReturnId:dutyReturnId.toString(),
    dutyReturnType:dutyReturnType.value,
    };
    const extractor = null;
    return this.makeJsonRequest("ullage/v1/claimSpoiltItemsOnDutyReturn", req, true, extractor, opts);
  };

  public async getUllages(ullageHeaderIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Ullage)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageHeaderIds:ullageHeaderIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.ullage.map((v: any) => new Ullage(v));;
    return this.makeJsonRequest("ullage/v1/getUllages", req, false, extractor, opts);
  };

  public async uncompleteUllage(ullageId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageId:ullageId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("ullage/v1/uncompleteUllage", req, true, extractor, opts);
  };

  public async getApproximateDutyForUllages(ullages: readonly Ullage[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UllageApproximateDuty)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullages:ullages.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new UllageApproximateDuty(v));;
    return this.makeJsonRequest("ullage/v1/getApproximateDutyForUllages", req, false, extractor, opts);
  };

  public async getApproximateDutyForUllageIds(ullageIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UllageApproximateDuty)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    ullageIds:ullageIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.results.map((v: any) => new UllageApproximateDuty(v));;
    return this.makeJsonRequest("ullage/v1/getApproximateDutyForUllageIds", req, false, extractor, opts);
  };
}

export const ullageService = new UllageService();